<script setup>
    import { defineProps, onMounted, ref, reactive, watch, computed } from "vue";
    import ChevronLeft from "../../images/SVG/IconSet/ChevronLeft.svg?raw";
    import ChevronRight from "../../images/SVG/IconSet/ChevronRight.svg?raw";
    import ArrowDown from "../../images/SVG/IconSet/ArrowDown.svg?raw";
    import ProductFinderOptionsGroup from "./ProductFinderOptionsGroup.vue";
    import { useStep1 } from "./steps/Step1.js";
    import { useStep2 } from "./steps/Step2.js";

    const history = ref([]);
    const activeStep = ref(null);

    // Imutable! passed from PHP
    const props = defineProps({
        ANZECCGuidelinesFile: String,
        RetrofitProjectFile: String,
        ContactNumber: String,
        ContactLink: String,
        AllProductsLink: String,
        Products: Array,
    });

    const activeProducts = ref([]);

    // Set defaults
    // Ok for testing but can break some things
    // better to let the app figure it out
    const projectType = ref("Private");
    const projectLocation = ref("");
    const projectUse = ref("");
    const projectTarget = ref("");
    const projectConstraint = ref("");
    const projectMessageTitle = ref("");
    const projectMessage = ref("");
    const projectMessageTitle2 = ref("");
    const projectMessage2 = ref("");
    const baselineMessage = ref("");

    // Unwrap some props and assign to state object for use in step builder later
    const { ANZECCGuidelinesFile, RetrofitProjectFile, ContactNumber, ContactLink } = props

    // It's nice just to wrap everything in a single object for passing around
    const results = reactive({
        type: projectType,
        location: projectLocation,
        use: projectUse,
        target: projectTarget,
        constraint: projectConstraint,
        messageTitle: projectMessageTitle,
        message: projectMessage,
        messageTitle2: projectMessageTitle2,
        message2: projectMessage2,
        baselineMessage: baselineMessage,
        activeProducts,
        ANZECCGuidelinesFile,
        RetrofitProjectFile,
        ContactNumber,
        ContactLink
    });

    const showBaseline = computed(() => baselineMessage.value !== "");

    const showProducts = computed(() => {
        return activeProducts.value.length > 0 && activeStep.value.index === 2
    })

    const showClearSelection = computed(() => {
        return (
            projectLocation.value != "" ||
            projectUse.value != "" ||
            projectTarget.value != "" ||
            projectConstraint.value != ""
        )
    });

    // This manages the top nav bar
    const step2Disabled = computed(() => {

        if (projectLocation.value === "") {
            return true
        }

        if (
            projectType.value === "" ||
            (projectLocation.value === "" && activeStep.value.index === 2)
        ) {
            return false;
        }

        // Because we track the history of indexes we know if the user has visited step 2
        if (history.value.map((index) => index).indexOf(2) > -1) {
            return false;
        }

        return true;
    });

    const showNext = computed(() => {
        if (activeStep.value === null) {
            return false;
        }

        if (activeStep.value.index === 1) {
            return true;
        }

        return false;
    });

    const canBack = computed(() => {
        if (activeStep.value.index === 2) {
            return true;
        }

        return false;
    });

    const canNext = computed(() => {
        if (activeStep.value.index === 1 && projectLocation.value != "") {
            return true;
        }

        return false;
    });

    const showMessage2 = computed(() => {
        return (
            activeStep.value.index === 2 &&
            projectMessageTitle2 != "" &&
            projectMessage2 != "" &&
            projectUse != "Standard"
        );
    });

    const showMessage = computed(() => {
        return (
            activeStep.value.index === 2 &&
            projectMessageTitle != "" &&
            projectMessage != "" &&
            projectUse != "Standard"
        );
    });

    const showPending = computed(() => {
        const { type, location, use, target, constraint } = results;

        if (activeStep.value.index !== 2) return false;

        if (use === "Industrial" || use === "State Highway") return false;

        if (use === "New Project" || use === "Retrofit Project") {
            const targetWithoutConstraints =
                target === "Green Infrastructure only" ||
                target === "Detention and Retention Requirement" ||
                target === "Pre-Treatment to primary treatment systems";

            if (!targetWithoutConstraints && constraint === "") {
                return true;
            }
        }

        if (type === "Private" && location === "Auckland") {
            const targetWithConstraints =
                target === "SMAF Zones" || target === "GD01 Level treatment";

            if (target === "" && constraint === "") {
                return true
            }

            if (targetWithConstraints && constraint === "") {
                return true;
            }
        }

        if (
            (type === "Private" && location === "Christchurch") ||
            (type === "Private" && location === "Waikato") ||
            (type === "Private" && location === "Other")
        ) {
            if (
                target === "" ||
                (target === "75% TSS Removal" && constraint === "")
            ) {
                return true;
            }
        }

        if (type === "Private" && location === "Wellington") {
            if (target === "") {
                return true;
            }
        }

        if (type === "Private" && location === "Canterbury") {
            if (use !== "Standard") {
                return false;
            }

            if (target === "") {
                return true;
            }

            const targetWithoutConstraints =
                target === "Detention and Retention Requirement" ||
                target === "Green Infrastructure only";

            if (!targetWithoutConstraints && constraint === "") {
                return true;
            }
        }

    });

    const showComplete = computed(() => {
        const { type, location, use, target, constraint } = results;

        if (activeStep.value.index !== 2) return false;

        if (use === "Industrial") return false;
        if (type === "Private" && use === "State Highway" && location != "") {
            return true;
        }

        if (use === "New Project" || use === "Retrofit Project") {
            const targetWithoutConstraints =
                target === "Green Infrastructure only" ||
                target === "Detention and Retention Requirement" ||
                target === "Pre-Treatment to primary treatment systems";

            if (targetWithoutConstraints && constraint === "") {
                return true;
            }

            if (!targetWithoutConstraints && constraint !== "") {
                return true;
            }
        }

        if (type === "Private" && location === "Auckland") {
            if (target === "") {
                return false;
            }

            const targetWithConstraints =
                target === "SMAF Zones" || target === "GD01 Level treatment";
            const constraintsWithProducts = constraint != "";

            if (targetWithConstraints && constraintsWithProducts) {
                return true;
            }

            if (!targetWithConstraints) {
                return true;
            }
        }

        if (
            (type === "Private" && location === "Christchurch") ||
            (type === "Private" && location === "Waikato") ||
            (type === "Private" && location === "Other")
        ) {
            const targetWithConstraints = target === "75% TSS Removal";
            const constraintsWithProducts =
                constraint === "Depth Constraints" || constraint === "None";

            if (targetWithConstraints && constraintsWithProducts) {
                return true;
            }

            if (!targetWithConstraints && target != "") {
                return true;
            }

            if (constraint != "") {
                return true
            }
        }

        if (type === "Private" && location === "Wellington") {
            if (target != "") {
                return true;
            }
        }

        if (type === "Private" && location === "Canterbury") {
            const targetWithoutConstraints =
                target === "Detention and Retention Requirement" ||
                target === "Green Infrastructure only";

            if (target != "" && targetWithoutConstraints) {
                return true;
            }

            if (constraint != "") {
                return true;
            }
        }
    });

    function filterProductsByID(ids) {
        return props.Products.filter((p) => ids.indexOf(p.ID) > -1);
    }

    function doNext() {
        // console.log('next')
        if (activeStep.value.index === 1) {
            showStep(2);
        }
    }

    // Takes in the index of the step to be loaded
    function showStep(index) {

        // console.log(activeStep.value)
        // console.log(index)
        // console.log(results)

        if (activeStep === null) return
        // If the active step is the same as the one being requested do nothing
        if (activeStep.value === index) return;

        if (index === 1) {
            // This is so dump we have to manually do this
            const step1 = useStep1(results);
            step1.questions[0].model = projectType;
            step1.questions[1].model = projectLocation;
            activeStep.value = step1;

            history.value.push(activeStep.value.index);
        }

        // Prevent going straight to step 2
        if (index === 2 && canNext.value && projectLocation.value != "") {
            loadStep2();
            history.value.push(activeStep.value.index);
        }
    }

    // Make a function because we do it in "showStep" and when projecttarget changes
    function loadStep2() {
        // console.log('loading step 2')
        const step2 = useStep2(results);

        step2.questions[0].model = projectUse;

        if (step2.questions[1]) {
            step2.questions[1].model = projectTarget;
        }

        if (step2.questions[2]) {
            step2.questions[2].model = projectConstraint;
        }

        activeStep.value = step2;
    }

    function checkProductAvailability() {
        const { type, location, use, target, constraint } = results;

        // Reset every time
        activeProducts.value = []
        let found = []

        // This just logs the products with key as ID and title as value for easy reference
        const r = {};
        for (let i = 0; i < props.Products.length; i++) {
            let c = props.Products[i];
            r[c.ID] = c.Title;
        }
        console.log(r)

        if (use === "Standard") {

            if (location === "Auckland") {
                if (target === "Detention & Retention Requirement") {
                    found = filterProductsByID([24, 26, 25]);
                }

                if (target === "SMAF Zones") {

                    if (constraint === "None") {
                        found = filterProductsByID([16, 28, 24]);
                    }

                    if (constraint === "Depth Constraints") {
                        found = filterProductsByID([16, 28, 26]);
                    }

                    if (constraint === "Hydraulic Constraints") {
                        found = filterProductsByID([16, 28, 15]);
                    }
                }

                if (target === "BioFiltration Only") {
                        found = filterProductsByID([28]);
                }

                if (target === "GD01 Level treatment") {

                    if (constraint === "Hydraulic Constraints") {
                        found = filterProductsByID([16, 28, 15]);
                    } else if (constraint !== "") {
                        found = filterProductsByID([16, 28]);
                    }
                }
            }

            if (location === "Christchurch" || location === "Waikato") {
                if (target === "75% TSS Removal") {
                    if (constraint === "None") {
                        found = filterProductsByID([16, 28]);
                    }
                    if (constraint === "Depth Constraints") {
                        found = filterProductsByID([28, 15]);
                    }
                    if (constraint === "Hydraulic Constraints") {
                        found = filterProductsByID([16, 28, 15]);
                    }
                } else if (target != "") {
                    found = filterProductsByID([24, 25, 26]);
                }
            }

            if (location === "Wellington") {
                if (target === "None") {
                    found = filterProductsByID([28]);
                }

                if (target === "Space Constraints") {
                    found = filterProductsByID([15, 16]);
                }
            }

            if (location === "Canterbury" || location === "Other") {
                if (target === "75% TSS Removal" || target === "ANZECC Guideline") {
                    if (constraint === "None") {
                        found = filterProductsByID([16, 28]);
                    } else if (constraint != "") {
                        found = filterProductsByID([15, 16, 28]);
                    }
                }
                if (target === "Green Infrastructure only") {
                    found = filterProductsByID([28, 26, 25]);
                }
                if (target === "BIO/GPT") {
                    if (constraint !="") {
                        if (constraint != "Depth Constraints") {
                            found = filterProductsByID([21]);
                        } else {
                            found = filterProductsByID([20]);
                        }
                    }
                }

                if (target === "Target Visible trash/neutral buoyant material") {
                    if (constraint === "None") {
                        found = filterProductsByID([19]);
                    } else if (constraint !== "") {
                        found = filterProductsByID([18]);
                    }
                }

                if (target === "Detention and Retention Requirement") {
                    found = filterProductsByID([24, 25, 26]);
                }
            }
        }

        if (use === "State Highway" && location === "Auckland") {
            found = filterProductsByID([16, 19]);
        }

        if (use === "State Highway" && location != "Auckland" && location != "") {
            found = filterProductsByID([16, 28, 21]);
        }

        if (use === "New Project" || use === "Retrofit Project") {
            // Location doesn't matter

            if (target === "75% TSS Removal" || target === "ANZECC Guideline") {

                if (constraint === "None") {
                    found = filterProductsByID([16, 28]);
                }

                if (constraint === "Physical Constraints") {
                    found = filterProductsByID([15, 16]);
                }

                if (constraint === "Budget Constraints") {
                    found = filterProductsByID([21, 19, 20]);
                }

                if (constraint === "Depth Constraints" || constraint === "Hydraulic Constraints") {
                    found = filterProductsByID([16, 15, 28]);
                }
            }

            if (target === "Green Infrastructure only") {
                found = filterProductsByID([28, 26, 25]);
            }

            if (target === "Coarse sediments") {
                if (constraint != "") {
                    if (constraint === "Depth Constraints") {
                        found = filterProductsByID([20]);
                    } else {
                        found = filterProductsByID([21]);
                    }
                }
            }

            if (target === "Target Visible trash/neutral buoyant material") {
                if (constraint != "") {
                    if (constraint === "None") {
                        found = filterProductsByID([19]);
                    } else {
                        found = filterProductsByID([18]);
                    }
                }
            }

            if (target === "Pre-Treatment to primary treatment systems") {
                found = filterProductsByID([21, 19, 20]);
            }

            if (target === "Detention and Retention Requirement") {
                found = filterProductsByID([25, 26, 24]);
            }
        }

        activeProducts.value = found;
    }

    watch(projectType, () => {
        // console.log('Type: ', projectType.value)
        projectUse.value = "";
        checkProductAvailability();
    });

    watch(projectLocation, () => {
        // console.log('Location: ', projectLocation.value)
        checkProductAvailability();
    })

    watch(projectUse , () => {
        checkProductAvailability();
        if (projectLocation.value != "" && projectUse.value != "" && projectLocation.value != "") {
            loadStep2();
        }
    });

    watch(projectTarget, () => {
        // console.log('Target: ', projectTarget.value)
        if (projectLocation.value != "" && projectUse.value != "" && projectLocation.value != "" && projectTarget.value != "") {
            projectConstraint.value = "";
            loadStep2();
            checkProductAvailability();
        }
    });

    watch(projectConstraint, () => {
        // console.log('Constraint: ', projectConstraint.value)
        if (projectLocation.value != "" && projectUse.value != "" && projectLocation.value != "" && projectTarget.value != "") {
            loadStep2();
            checkProductAvailability();
        }
    })

    onMounted(() => {
        showStep(1);
    });

    function scrollToResults(e) {
        e.preventDefault()
        document.querySelector('#product-results__container').scrollIntoView()
    }

    function clearSelection() {
        projectType.value = "Private"
        projectLocation.value = ""
        projectUse.value = ""
        projectTarget.value = ""
        projectConstraint.value = ""

        results.activeProducts = []
        results.messageTitle = "",
        results.message = ""
        results.messageTitle2 = "",
        results.message2 = ""
        results.baselineMessage = ""

        showStep(1)

        document.querySelector('#product-finder').scrollIntoView()
    }

    function getBackground(Thumbnail) {
        if (Thumbnail === null) return ""
        return `background-image: url(${Thumbnail})`
    }

</script>

<template>
    <div id="product-finder">
        <div class="product-finder__wrapper">
            <div class="product-finder__container" v-if="activeStep">
                <div class="product-finder__nav">
                    <label @click="showStep(1)"
                        ><input type="radio" name="screen" checked /> 1. Project
                        type and location</label
                    >
                    <label @click="showStep(2)"
                        ><input
                            type="radio"
                            name="screen"
                            :disabled="step2Disabled"
                        />
                        2. Environmental targets and constraints</label
                    >
                </div>
                <div class="product-finder__title">{{ activeStep.title }}</div>
                <div v-if="projectLocation" class="product-finder__chosen">
                    <span @click="showStep(1)">{{ projectType }}</span> project
                    in <span @click="showStep(1)">{{ projectLocation }}</span>
                </div>
                <div class="product-finder__options">
                    <ProductFinderOptionsGroup
                        v-for="group in activeStep.questions"
                        v-bind="group"
                        v-model="group.model"
                    />
                    <div class="product-finder__message" v-if="showMessage2">
                        <p class="product-finder__message-title">
                            {{ projectMessageTitle2 }}
                        </p>
                        <div
                            class="product-finder__message-content"
                            v-html="projectMessage2"
                        ></div>
                    </div>
                    <div class="product-finder__message" v-if="showMessage">
                        <p class="product-finder__message-title">
                            {{ projectMessageTitle }}
                        </p>
                        <div
                            class="product-finder__message-content"
                            v-html="projectMessage"
                        ></div>
                    </div>
                </div>
                <div class="product-finder__footer">
                    <button
                        v-if="canBack"
                        @click="showStep(1)"
                        class="btn-outline-mid-grey-100-transparent-icon"
                    >
                        <div v-html="ChevronLeft"></div>
                        <span>Back</span>
                    </button>
                    <div>
                        <button v-if="showClearSelection" @click="clearSelection" class="underline-link">
                            Clear Selection
                        </button>
                        <a @click="scrollToResults" href="#product-results__container" v-if="showComplete" class="btn-navy-100-icon">
                            <span>See Results</span>
                            <div v-html="ArrowDown"></div>
                        </a>
                        <button
                            v-if="showNext"
                            :disabled="!canNext"
                            @click="doNext"
                            class="btn-navy-100-icon"
                        >
                            <span>Next</span>
                            <div v-html="ChevronRight"></div>
                        </button>
                        <button
                            v-if="showPending"
                            class="btn-outline-white-100"
                            disabled
                        >
                            <span
                                >Complete selection <span>to see product options</span></span
                            >
                        </button>
                    </div>
                </div>
                <div
                    v-if="showBaseline"
                    class="product-finder__baseline"
                    v-html="baselineMessage"
                ></div>
            </div>
        </div>
        <div
            id="product-results__container"
            class="product-results__container"
            v-if="activeProducts.length > 0 && showProducts"
        >
            <div class="container">
                <div class="product-results__actionbar">
                    <div class="product-results__titlebar">
                        <p class="product-results__title">
                            <span>{{ activeProducts.length }} result<span v-if="activeProducts.length > 1">s</span></span> for
                            you to consider
                        </p>
                        <p class="product-results__description">
                            Contact your local council to confirm the technology
                            meets compliance for your desired application.
                        </p>
                    </div>
                    <div class="product-results__actions">
                        <a :href="AllProductsLink" class="btn-mid-grey-100-icon"
                            ><span>View all products</span
                            ><span v-html="ChevronRight"></span
                        ></a>
                    </div>
                </div>
                <div class="product-cards-group__list">
                    <a
                        class="product-card"
                        v-for="p in activeProducts"
                        :href="p.Link"
                    >
                        <div class="product-card__background" :style="getBackground(p.Thumbnail)"></div>
                        <div class="product-card__container">
                            <div class="product-card__content">
                                <span class="product-card__title">{{
                                    p.Title
                                }}</span>
                                <div class="product-card__details" v-if="p.ThumbnailDescription">
                                    <span
                                        >{{ p.ThumbnailDescription }}</span
                                    >
                                    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.0306 8.53074L9.53063 13.0307C9.38973 13.1716 9.19863 13.2508 8.99938 13.2508C8.80012 13.2508 8.60902 13.1716 8.46813 13.0307C8.32723 12.8898 8.24807 12.6987 8.24807 12.4995C8.24807 12.3002 8.32723 12.1091 8.46813 11.9682L11.6875 8.75011H2.5C2.30109 8.75011 2.11032 8.67109 1.96967 8.53044C1.82902 8.38979 1.75 8.19902 1.75 8.00011C1.75 7.8012 1.82902 7.61043 1.96967 7.46978C2.11032 7.32913 2.30109 7.25011 2.5 7.25011H11.6875L8.46937 4.03011C8.32848 3.88921 8.24932 3.69812 8.24932 3.49886C8.24932 3.2996 8.32848 3.10851 8.46937 2.96761C8.61027 2.82671 8.80137 2.74756 9.00062 2.74756C9.19988 2.74756 9.39098 2.82671 9.53187 2.96761L14.0319 7.46761C14.1018 7.53738 14.1573 7.62028 14.1951 7.71155C14.2329 7.80281 14.2523 7.90065 14.2522 7.99943C14.252 8.09822 14.2324 8.19601 14.1944 8.28718C14.1564 8.37836 14.1007 8.46113 14.0306 8.53074Z" fill="currentColor"/>
                                    </svg>

                                </div>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
